import React, { useState } from "react";
import { CalendarDays, Clock, Sparkles, Loader2, Send } from "lucide-react";
import { PageHero } from "@/components/Bits";
import { Reveal } from "@/components/Reveal";
import { VideoEmbed } from "@/components/VideoEmbed";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { JOURNAL_POSTS } from "@/lib/journal";
import { api } from "@/lib/api";
import { toast } from "sonner";

const fmtDate = (d) => new Date(d + "T00:00:00").toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

export default function Journal() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) { toast.error("Please enter your email."); return; }
    setLoading(true);
    try {
      await api.post("/subscribe", { email, tag: "journal" });
      toast.success("You're on the list — new reflections will find you. 💛");
      setEmail("");
    } catch { toast.error("Something went wrong. Please try again."); }
    setLoading(false);
  };

  return (
    <div data-testid="journal-page">
      <PageHero
        eyebrow="The Storyteller's Journal"
        title="Words from the heart, shared each week"
        subtitle="Reflections, stories, and gentle encouragement from our founder — a storyteller at heart. New posts weekly, and more often as our community grows."
        image={JOURNAL_POSTS[0]?.image}
        testid="journal-hero"
      />

      <section className="max-w-3xl mx-auto px-4 py-16 sm:py-20 space-y-16">
        {JOURNAL_POSTS.map((post, i) => (
          <Reveal key={post.id}>
            <article data-testid={`journal-post-${post.id}`}>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-bsn-bronze mb-4">
                <span className="eyebrow">{post.category}</span>
                <span className="flex items-center gap-1.5 text-bsn-sapphire/70"><CalendarDays className="w-4 h-4" /> {fmtDate(post.date)}</span>
                <span className="flex items-center gap-1.5 text-bsn-sapphire/70"><Clock className="w-4 h-4" /> {post.readTime}</span>
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-bsn-midnight leading-tight">{post.title}</h2>

              {post.videoUrl ? (
                <div className="mt-6"><VideoEmbed url={post.videoUrl} title={post.title} poster={post.image} /></div>
              ) : post.image ? (
                <img src={post.image} alt={post.title} className="mt-6 w-full h-72 object-cover rounded-2xl card-tactile" />
              ) : null}

              <div className="mt-6 space-y-4 text-lg leading-relaxed text-bsn-sapphire/90">
                <img src="/speaker/founder.webp" alt="Bright Stories Network founder" className="float-left w-24 h-24 rounded-xl object-cover object-top mr-4 mb-2 mt-1 border-2 border-bsn-gold/60 shadow-md" data-testid={`journal-founder-photo-${post.id}`} />
                {post.body.map((p, k) => <p key={k}>{p}</p>)}
                <div className="clear-both" />
              </div>
            </article>
            {i < JOURNAL_POSTS.length - 1 && <div className="gold-rule mt-16" />}
          </Reveal>
        ))}
      </section>

      <section className="bg-bsn-midnight text-bsn-parchment">
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <Sparkles className="w-9 h-9 text-bsn-gold mx-auto mb-4" />
          <h3 className="font-serif text-2xl sm:text-3xl font-semibold">Never miss a reflection</h3>
          <p className="mt-3 text-bsn-ice/80">Get each new journal entry gently delivered to your inbox.</p>
          <form onSubmit={subscribe} className="mt-6 flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-testid="journal-subscribe-form">
            <Input type="email" placeholder="Your email" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-bsn-navy border-bsn-gold/30 text-bsn-parchment placeholder:text-bsn-ice/40" data-testid="journal-email-input" />
            <Button type="submit" disabled={loading} className="btn-gold font-semibold shrink-0" data-testid="journal-subscribe-btn">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> Subscribe</>}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
}
